import React from "react";
import SettingsSection from "../SettingsSection";
import ChannelPicker from "../ChannelPicker";

export default function AutoModSection({
  channels,
  forbiddenWords,
  setForbiddenWords,
  newWord,
  setNewWord,
  antiLinks,
  setAntiLinks,
  linkBlacklist,
  setLinkBlacklist,
  antiLinkExemptChannelIds = [],
  setAntiLinkExemptChannelIds,
  antiLinkExemptCategoryIds = [],
  setAntiLinkExemptCategoryIds,
  antiSpamEnabled = false,
  setAntiSpamEnabled,
  antiSpamMaxMessages = 5,
  setAntiSpamMaxMessages,
  antiSpamIntervalSeconds = 5,
  setAntiSpamIntervalSeconds,
  antiSpamExemptChannelIds = [],
  setAntiSpamExemptChannelIds,
  antiSpamExemptCategoryIds = [],
  setAntiSpamExemptCategoryIds,
  newLink,
  setNewLink,
  maxMentions,
  setMaxMentions,
  commandToggles,
  setCommandToggles,
  security,
  setSecurity,
  falseGate,
  setFalseGate,
  setHasUnsavedChanges,
}) {
  const markDirty = () => {
    if (setHasUnsavedChanges) setHasUnsavedChanges(true);
  };

  return (
    <SettingsSection>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Automatic message filters and join guard: forbidden words, links,
        mention limits, spam, and raid/alt detection. Also configurable via{" "}
        <code>/settings</code> and <code>/blockword</code> in Discord.
      </p>

      <div
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "20px",
          padding: "15px",
          background: "rgba(184, 92, 110, 0.05)",
          borderRadius: "8px",
          border: "1px solid rgba(184, 92, 110, 0.1)",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={Boolean(commandToggles?.autoModEnabled)}
            onChange={() => {
              setCommandToggles((prev) => ({
                ...prev,
                autoModEnabled: !prev.autoModEnabled,
              }));
              markDirty();
            }}
            style={{ accentColor: "#b85c6e" }}
          />
          <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            AUTOMOD ENABLED
          </span>
        </label>
      </div>

      <div
        style={{
          opacity: commandToggles?.autoModEnabled ? 1 : 0.5,
          pointerEvents: commandToggles?.autoModEnabled ? "all" : "none",
        }}
      >
        <label className="input-label">FORBIDDEN WORDS</label>
        <div className="word-input-group" style={{ marginBottom: "15px" }}>
          <input
            type="text"
            className="settings-input"
            style={{ marginBottom: 0 }}
            value={newWord}
            onChange={(e) => setNewWord(e.target.value)}
            placeholder="Add a word..."
          />
          <button
            type="button"
            className="manage-btn"
            style={{ width: "80px", marginTop: 0, height: "54px" }}
            onClick={() => {
              if (newWord && !forbiddenWords.includes(newWord.toLowerCase())) {
                setForbiddenWords([...forbiddenWords, newWord.toLowerCase()]);
                setNewWord("");
                markDirty();
              }
            }}
          >
            ADD
          </button>
        </div>
        <div className="word-tags" style={{ marginBottom: "15px" }}>
          {forbiddenWords.map((word) => (
            <span key={word} className="word-tag">
              {word}
              <button
                type="button"
                onClick={() => {
                  setForbiddenWords(forbiddenWords.filter((w) => w !== word));
                  markDirty();
                }}
              >
                ×
              </button>
            </span>
          ))}
        </div>

        <label className="input-label">LINK PROTECTION</label>
        <div className="toggle-group" style={{ marginBottom: "15px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              color: antiLinks ? "#b85c6e" : "inherit",
            }}
          >
            <input
              type="checkbox"
              checked={antiLinks}
              onChange={(e) => {
                setAntiLinks(e.target.checked);
                markDirty();
              }}
            />
            <b>BAN ALL LINKS</b>
          </label>
        </div>

        {!antiLinks && (
          <>
            <p
              className="help-text"
              style={{
                color: "#7b6f62",
                fontSize: "0.8rem",
                marginBottom: "15px",
              }}
            >
              &quot;Ban All&quot; is off. You can blacklist specific domains
              (e.g., discord.gg, bit.ly) below:
            </p>
            <div className="word-input-group" style={{ marginBottom: "15px" }}>
              <input
                type="text"
                className="settings-input"
                style={{ marginBottom: 0 }}
                value={newLink}
                onChange={(e) => setNewLink(e.target.value)}
                placeholder="e.g. discord.gg"
              />
              <button
                type="button"
                className="manage-btn"
                style={{ width: "80px", marginTop: 0, height: "54px" }}
                onClick={() => {
                  if (
                    newLink &&
                    !linkBlacklist.includes(newLink.toLowerCase())
                  ) {
                    setLinkBlacklist([
                      ...linkBlacklist,
                      newLink.toLowerCase(),
                    ]);
                    setNewLink("");
                    markDirty();
                  }
                }}
              >
                ADD
              </button>
            </div>
            <div className="word-tags">
              {linkBlacklist.map((link) => (
                <span key={link} className="word-tag">
                  {link}
                  <button
                    type="button"
                    onClick={() => {
                      setLinkBlacklist(
                        linkBlacklist.filter((l) => l !== link),
                      );
                      markDirty();
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </>
        )}

        <label className="input-label" style={{ marginTop: "20px" }}>
          LINK / INVITE EXCEPTIONS
        </label>
        <p
          className="help-text"
          style={{
            color: "#7b6f62",
            fontSize: "0.8rem",
            marginBottom: "12px",
          }}
        >
          Skip invite and link automod in these channels or whole categories
          (useful for partner / invite boards). Members with Manage Messages are
          always exempt.
        </p>

        <label className="input-label">EXEMPT CHANNELS</label>
        <ChannelPicker
          channels={channels}
          multiple
          value={antiLinkExemptChannelIds}
          onChange={(ids) => {
            if (!setAntiLinkExemptChannelIds) return;
            setAntiLinkExemptChannelIds(ids);
            markDirty();
          }}
          placeholder="Select channels..."
        />

        <label className="input-label">EXEMPT CATEGORIES</label>
        <ChannelPicker
          channels={channels}
          multiple
          kind="category"
          value={antiLinkExemptCategoryIds}
          onChange={(ids) => {
            if (!setAntiLinkExemptCategoryIds) return;
            setAntiLinkExemptCategoryIds(ids);
            markDirty();
          }}
          placeholder="Select categories..."
        />

        <label className="input-label" style={{ marginTop: "15px" }}>
          MAX MENTIONS PER MESSAGE
        </label>
        <input
          type="number"
          className="settings-input"
          value={maxMentions}
          onChange={(e) => {
            setMaxMentions(parseInt(e.target.value) || 0);
            markDirty();
          }}
          placeholder="0 to disable"
        />

        <label className="input-label" style={{ marginTop: "20px" }}>
          SPAM PREVENTION
        </label>
        <p
          className="help-text"
          style={{
            color: "#7b6f62",
            fontSize: "0.8rem",
            marginBottom: "12px",
          }}
        >
          Deletes and warns when someone sends too many messages too quickly.
          Members with Manage Messages are always exempt. Requires Automod
          Enabled above.
        </p>
        <div className="toggle-group" style={{ marginBottom: "15px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              color: antiSpamEnabled ? "#b85c6e" : "inherit",
            }}
          >
            <input
              type="checkbox"
              checked={Boolean(antiSpamEnabled)}
              onChange={(e) => {
                setAntiSpamEnabled(e.target.checked);
                markDirty();
              }}
            />
            <b>ENABLE SPAM PREVENTION</b>
          </label>
        </div>

        {antiSpamEnabled && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
                marginBottom: "12px",
              }}
            >
              <div>
                <label className="input-label">MAX MESSAGES</label>
                <input
                  type="number"
                  className="settings-input"
                  min={2}
                  max={30}
                  value={antiSpamMaxMessages}
                  onChange={(e) => {
                    setAntiSpamMaxMessages(
                      Math.max(
                        2,
                        Math.min(30, parseInt(e.target.value) || 5),
                      ),
                    );
                    markDirty();
                  }}
                />
              </div>
              <div>
                <label className="input-label">WITHIN (SECONDS)</label>
                <input
                  type="number"
                  className="settings-input"
                  min={1}
                  max={60}
                  value={antiSpamIntervalSeconds}
                  onChange={(e) => {
                    setAntiSpamIntervalSeconds(
                      Math.max(
                        1,
                        Math.min(60, parseInt(e.target.value) || 5),
                      ),
                    );
                    markDirty();
                  }}
                />
              </div>
            </div>
            <p
              className="help-text"
              style={{
                color: "#7b6f62",
                fontSize: "0.8rem",
                marginBottom: "12px",
              }}
            >
              Default: more than <b>{antiSpamMaxMessages}</b> messages in{" "}
              <b>{antiSpamIntervalSeconds}s</b> triggers a delete + warn.
            </p>

            <label className="input-label">SPAM EXEMPT CHANNELS</label>
            <p
              className="help-text"
              style={{
                color: "#7b6f62",
                fontSize: "0.8rem",
                marginBottom: "8px",
              }}
            >
              Skip spam checks here (counting, bot-commands, media dumps,
              etc.).
            </p>
            <ChannelPicker
              channels={channels}
              multiple
              value={antiSpamExemptChannelIds}
              onChange={(ids) => {
                if (!setAntiSpamExemptChannelIds) return;
                setAntiSpamExemptChannelIds(ids);
                markDirty();
              }}
              placeholder="Select channels..."
            />

            <label className="input-label">SPAM EXEMPT CATEGORIES</label>
            <ChannelPicker
              channels={channels}
              multiple
              kind="category"
              value={antiSpamExemptCategoryIds}
              onChange={(ids) => {
                if (!setAntiSpamExemptCategoryIds) return;
                setAntiSpamExemptCategoryIds(ids);
                markDirty();
              }}
              placeholder="Select categories..."
            />
          </>
        )}
      </div>

      <hr
        style={{
          border: "0",
          borderTop: "1px solid rgba(123, 111, 98, 0.2)",
          margin: "28px 0 20px",
        }}
      />

      <label className="input-label">JOIN GUARD</label>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        Flags real user accounts that look like spam/raid alts (young accounts,
        default avatars, suspicious names, join floods). Not Discord app bots.
        Jail action needs <code>/setup-jail</code>.
      </p>

      <div
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "16px",
          padding: "15px",
          background: "rgba(192, 57, 43, 0.06)",
          borderRadius: "8px",
          border: "1px solid rgba(192, 57, 43, 0.15)",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={Boolean(security?.enabled)}
            onChange={() => {
              setSecurity((prev) => ({
                ...prev,
                enabled: !prev.enabled,
              }));
              markDirty();
            }}
            style={{ accentColor: "#c0392b" }}
          />
          <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            SECURITY ENABLED
          </span>
        </label>
      </div>

      <div
        style={{
          opacity: security?.enabled ? 1 : 0.45,
          pointerEvents: security?.enabled ? "all" : "none",
        }}
      >
        <label className="input-label">ACTION ON FLAG</label>
        <select
          className="settings-input"
          value={security?.action || "jail"}
          onChange={(e) => {
            setSecurity((prev) => ({ ...prev, action: e.target.value }));
            markDirty();
          }}
        >
          <option value="jail">Jail (default)</option>
          <option value="kick">Kick</option>
          <option value="log">Log only</option>
        </select>

        <label className="input-label">MINIMUM ACCOUNT AGE (DAYS)</label>
        <p
          className="help-text"
          style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "10px" }}
        >
          Counts as one suspicion signal. Set 0 to ignore age.
        </p>
        <input
          type="number"
          min="0"
          className="settings-input"
          value={security?.minAccountAgeDays ?? 7}
          onChange={(e) => {
            setSecurity((prev) => ({
              ...prev,
              minAccountAgeDays: parseInt(e.target.value, 10) || 0,
            }));
            markDirty();
          }}
        />

        <label className="input-label">SCORE THRESHOLD</label>
        <p
          className="help-text"
          style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "10px" }}
        >
          How many signals (age / default avatar / name) needed to act. Join
          flood always acts.
        </p>
        <input
          type="number"
          min="1"
          className="settings-input"
          value={security?.scoreThreshold ?? 2}
          onChange={(e) => {
            setSecurity((prev) => ({
              ...prev,
              scoreThreshold: Math.max(1, parseInt(e.target.value, 10) || 1),
            }));
            markDirty();
          }}
        />

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "16px",
          }}
        >
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={security?.blockDefaultAvatar !== false}
              onChange={() => {
                setSecurity((prev) => ({
                  ...prev,
                  blockDefaultAvatar: !prev.blockDefaultAvatar,
                }));
                markDirty();
              }}
              style={{ accentColor: "#c0392b" }}
            />
            <span style={{ fontSize: "0.85rem" }}>Default avatar signal</span>
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={security?.suspiciousNames !== false}
              onChange={() => {
                setSecurity((prev) => ({
                  ...prev,
                  suspiciousNames: !prev.suspiciousNames,
                }));
                markDirty();
              }}
              style={{ accentColor: "#c0392b" }}
            />
            <span style={{ fontSize: "0.85rem" }}>Suspicious name signal</span>
          </label>
        </div>

        <label className="input-label">JOIN FLOOD</label>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "12px",
          }}
        >
          <div>
            <p
              className="help-text"
              style={{
                color: "#7b6f62",
                fontSize: "0.8rem",
                marginBottom: "8px",
              }}
            >
              Max joins
            </p>
            <input
              type="number"
              min="1"
              className="settings-input"
              value={security?.joinFloodCount ?? 5}
              onChange={(e) => {
                setSecurity((prev) => ({
                  ...prev,
                  joinFloodCount: Math.max(
                    1,
                    parseInt(e.target.value, 10) || 1,
                  ),
                }));
                markDirty();
              }}
            />
          </div>
          <div>
            <p
              className="help-text"
              style={{
                color: "#7b6f62",
                fontSize: "0.8rem",
                marginBottom: "8px",
              }}
            >
              Within seconds
            </p>
            <input
              type="number"
              min="1"
              className="settings-input"
              value={security?.joinFloodSeconds ?? 10}
              onChange={(e) => {
                setSecurity((prev) => ({
                  ...prev,
                  joinFloodSeconds: Math.max(
                    1,
                    parseInt(e.target.value, 10) || 1,
                  ),
                }));
                markDirty();
              }}
            />
          </div>
        </div>
      </div>

      <hr
        style={{
          border: "0",
          borderTop: "1px solid rgba(123, 111, 98, 0.2)",
          margin: "28px 0 20px",
        }}
      />

      <label className="input-label">FALSE GATE</label>
      <p
        className="help-text"
        style={{ color: "#7b6f62", fontSize: "0.8rem", marginBottom: "15px" }}
      >
        A bait channel that looks open. Saving posts (or refreshes) a warning
        embed that updates its <b>Accounts Caught</b> counter each time someone
        trips it. Staff with mod permissions are ignored by default.
      </p>
      {Number(falseGate?.caughtCount) > 0 ? (
        <p
          className="help-text"
          style={{ color: "#b85c6e", fontSize: "0.85rem", marginBottom: "12px" }}
        >
          Accounts caught so far: <b>{falseGate.caughtCount}</b>
        </p>
      ) : null}

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "12px",
          padding: "16px",
          background: "rgba(192, 57, 43, 0.05)",
          borderRadius: "8px",
          border: "1px solid rgba(192, 57, 43, 0.12)",
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
          }}
        >
          <input
            type="checkbox"
            checked={Boolean(falseGate?.enabled)}
            onChange={() => {
              setFalseGate((prev) => ({
                ...prev,
                enabled: !prev.enabled,
              }));
              markDirty();
            }}
            style={{ accentColor: "#c0392b" }}
          />
          <span style={{ fontSize: "0.9rem", fontWeight: "bold" }}>
            ENABLE FALSE GATE
          </span>
        </label>

        <div
          style={{
            opacity: falseGate?.enabled ? 1 : 0.45,
            pointerEvents: falseGate?.enabled ? "all" : "none",
            display: "grid",
            gap: "12px",
          }}
        >
          <div>
            <label className="input-label">GATE CHANNEL</label>
            <ChannelPicker
              channels={channels}
              value={falseGate?.channelId || ""}
              onChange={(channelId) => {
                setFalseGate((prev) => ({ ...prev, channelId }));
                markDirty();
              }}
              emptyLabel="Select bait channel..."
            />
          </div>

          <div>
            <label className="input-label">ACTION</label>
            <select
              className="settings-input"
              value={falseGate?.action || "ban"}
              onChange={(e) => {
                setFalseGate((prev) => ({
                  ...prev,
                  action: e.target.value,
                }));
                markDirty();
              }}
            >
              <option value="ban">Ban</option>
              <option value="softban">Softban (purge messages, not lasting)</option>
              <option value="kick">Kick</option>
              <option value="jail">Jail</option>
              <option value="timeout">Timeout</option>
              <option value="log">Log only</option>
            </select>
          </div>

          {falseGate?.action === "timeout" ? (
            <div>
              <label className="input-label">TIMEOUT (MINUTES)</label>
              <input
                type="number"
                min="1"
                max="40320"
                className="settings-input"
                value={falseGate?.timeoutMinutes ?? 60}
                onChange={(e) => {
                  setFalseGate((prev) => ({
                    ...prev,
                    timeoutMinutes: Math.max(
                      1,
                      Math.min(40320, parseInt(e.target.value, 10) || 60),
                    ),
                  }));
                  markDirty();
                }}
              />
            </div>
          ) : null}

          <div>
            <label className="input-label">REASON (MOD LOG)</label>
            <input
              type="text"
              className="settings-input"
              value={falseGate?.reason || ""}
              placeholder="Spoke in the False Gate."
              onChange={(e) => {
                setFalseGate((prev) => ({
                  ...prev,
                  reason: e.target.value,
                }));
                markDirty();
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={falseGate?.deleteMessage !== false}
                onChange={() => {
                  setFalseGate((prev) => ({
                    ...prev,
                    deleteMessage: !prev.deleteMessage,
                  }));
                  markDirty();
                }}
                style={{ accentColor: "#c0392b" }}
              />
              <span style={{ fontSize: "0.85rem" }}>Delete bait message</span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={falseGate?.ignoreStaff !== false}
                onChange={() => {
                  setFalseGate((prev) => ({
                    ...prev,
                    ignoreStaff: !prev.ignoreStaff,
                  }));
                  markDirty();
                }}
                style={{ accentColor: "#c0392b" }}
              />
              <span style={{ fontSize: "0.85rem" }}>Ignore staff</span>
            </label>
          </div>
        </div>
      </div>
    </SettingsSection>
  );
}
